import { UploadListContainer } from '@components/pages/mercado-livre-de-energia/section-form-banner/free-market-lead-form/multiple-file-upload-container/upload-list-container'
import { useField } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { FileError, FileRejection, useDropzone } from 'react-dropzone'
import { BsPaperclip } from 'react-icons/bs'
import { FaCloudUploadAlt } from 'react-icons/fa'
import { SingleFileUploadWithProgress } from './single-file-upload-with-progress'

export interface UploadableFile {
  file: File
  errors: FileError[]
  url?: string
}

interface Props {
  name: string
  resetList?: boolean
  uploadingState: (condition: boolean) => void
}

export const MultipleFileUploadContainer = ({
  name,
  resetList,
  uploadingState
}: Props) => {
  const [_, __, helpers] = useField(name)
  const [files, setFiles] = useState<UploadableFile[]>([])

  const kbValue = 1024 // 1KB
  const mbValue = 1048576 // 1 MB
  const minFileSize = 10 * kbValue // 10 KB
  const maxFileSize = 10 * mbValue // 10 MB

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      uploadingState(true)
      const mappedAccepted = acceptedFiles.map(file => ({ file, errors: [] }))
      const mappedRejected = rejectedFiles.map(file => {
        file.errors.map(error => {
          if (error.code === 'file-invalid-type') {
            error.message =
              'Tipos de arquivos permitidos: JPEG/JPG/PNG/HEIC/PDF'
          }

          if (error.code === 'file-too-small') {
            error.message = `O arquivo é menor que o tamanho mínimo permitido: ${
              minFileSize / kbValue
            } KB`
          }

          if (error.code === 'file-too-large') {
            error.message = `O arquivo excede o tamanho máximo permitido: ${
              maxFileSize / mbValue
            } MB`
          }
        })

        return file
      })
      setFiles(current => [...current, ...mappedAccepted, ...mappedRejected])
    },
    []
  )
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/heic',
      'application/pdf'
    ],
    minSize: minFileSize,
    maxSize: maxFileSize
  })

  const onUpload = (file: File, url: string) => {
    setFiles(current =>
      current.map(fileWrapper => {
        if (fileWrapper.file === file) {
          return { ...fileWrapper, url }
        }
        return fileWrapper
      })
    )
  }

  const onDelete = (fileName: string) => {
    setFiles(current =>
      current.filter(fileWrapper => fileWrapper.file.name !== fileName)
    )
  }

  useEffect(() => {
    const fileWithErrors = files?.filter(file => file.errors.length > 0)
    const uploadedFiles = files?.filter(file => file.url)

    if (fileWithErrors.length <= 0 && uploadedFiles.length === files.length) {
      uploadingState(false)
      helpers.setValue(files)
    }
  }, [files])

  useEffect(() => {
    if (resetList) setFiles([])
  }, [resetList])

  return (
    <>
      <div
        {...getRootProps()}
        className="hidden items-center justify-center bg-gray-50 md:border-2 md:border-v2-sonicSilver md:border-dashed md:rounded-md pt-14 pb-8"
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center">
          <FaCloudUploadAlt size={129} className="text-gray-300" />
          <p className="font-workSans font-light md:text-xl">
            Arraste Solte ou{' '}
            <span className="text-blue font-medium">Clique Aqui</span> para
            escolher arquivo
          </p>
        </div>
      </div>

      <div
        onClick={open}
        className="flex justify-center items-center text-v2-bleu-de-france cursor-pointer gap-2 lg:flex-col xl:flex-row"
      >
        <div className="flex items-center gap-1">
          <BsPaperclip className="text-xl sm:text-3xl" />
          <p className="font-workSans text-sm sm:text-lg">
            Anexe uma ou mais contas de Luz
          </p>
        </div>
        <p className="text-xs text-v2-sonic-silver font-semibold text-center sm:text-lg sm:text-v2-gray-x11">
          (opcional)
        </p>
      </div>

      {files.length !== 0 && (
        <div className="w-full flex flex-col gap-4 max-w-sm">
          <UploadListContainer className="mt-5">
            {files.map(fileWrapper => (
              <SingleFileUploadWithProgress
                key={fileWrapper.file.name}
                file={fileWrapper.file}
                onUpload={onUpload}
                onDelete={onDelete}
                errors={fileWrapper.errors}
                className={
                  fileWrapper.file.name !== files[0].file.name && 'mt-6'
                }
              />
            ))}
          </UploadListContainer>
          <div className="flex flex-col gap-4">
            {files.map(
              el =>
                el.errors.length !== 0 &&
                el.errors.map(error => (
                  <p
                    key={el.file.lastModified}
                    className="font-workSans text-red text-xs sm:text-sm"
                  >
                    {el.file.name} - {error.message}
                  </p>
                ))
            )}
          </div>
        </div>
      )}
    </>
  )
}
